<script setup lang="ts">
import { computed } from 'vue';
import ChatMessageWrapper from '../ChatMessageWrapper.vue';
import PaymentTransactionMessage from './PaymentTransactionMessage.vue';
import BetTransactionMessage from './BetTransactionMessage.vue';
import type { TransactionSelectEvent, TransactionSubmitEvent, FullTransaction } from './types';
import { TransactionSelectMessage } from '@/common/bot/types';
import { buildExternalEvent } from '@/common/bot/lib/event.factory';
import { ExternalBotEvent } from '@/common/bot/enum/ExternalBotEvent';
import type {
  ApplyMetadataAutomationExternEvent,
  UserSelectedTransactionListEvent,
} from '@/common/bot/types/events/external/external-events';
import { TransactionType } from '@/common/config';
import { ExternalTransactionKind } from '@/common/bot/enum/ExternalTransactionType';
import { getTransactionTypeFromSubtype, isBetTransactionType, isPaymentTransaction } from '@/common/transaction-utils';

export interface SelectTransactionMessageProps {
  /**
   * The message to render.
   */
  message: TransactionSelectMessage;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;
}

const props = defineProps<SelectTransactionMessageProps>();

const emit = defineEmits<{
  (e: 'message:input', event: UserSelectedTransactionListEvent): void;
  (e: 'external-event', event: ApplyMetadataAutomationExternEvent): void;
  (e: 'scroll-bottom'): void;
}>();

// If there is no next message, then we can submit
const canSubmit = computed(() => !props.next);
const cancelable = computed(() => props.message.cancelable);
const connectConfig = computed(() => props.message.config?.connect);
const showEventSelection = computed(() => props.message.config?.connect?.aiConversation);
const transactionSubtypes = computed(() => {
  // Check for overwrite from message params
  if (props.message.txSubtypes) {
    return props.message.txSubtypes;
  }

  // Check for types array
  if (connectConfig.value?.transactionTypes) {
    return connectConfig.value.transactionTypes.map((type) => type as ExternalTransactionKind);
  }
  // Check for transactionSubtypes
  if (connectConfig.value?.transactionType) {
    return [connectConfig.value.transactionType as ExternalTransactionKind];
  }

  return [];
});

const transactionType = computed(() => {
  return getTransactionTypeFromSubtype(transactionSubtypes.value[0]);
});

// Determine if this is a bet transaction
const isBetTransaction = computed(() => {
  return transactionType.value === TransactionType.Bet;
});

// Get pre-selected transactions from message
const preSelectedTransactions = computed(() => {
  return (props.message.preSelectedTransactions || []).filter(isPaymentTransaction);
});

// Get pre-selected bets from message
const preSelectedBets = computed(() => {
  return (props.message.preSelectedTransactions || []).filter(isBetTransactionType);
});

const handleSubmit = (event: TransactionSubmitEvent) => {
  const selectedTransactionEvent = buildExternalEvent<UserSelectedTransactionListEvent>({
    type: ExternalBotEvent.USER_SELECTED_TRANSACTION_LIST,
    payload: {
      transactions: event.transactions as FullTransaction[],
      transactionType: transactionType.value,
    },
  });

  emit('message:input', selectedTransactionEvent);
};

const handleMetadataAutomation = (event: ApplyMetadataAutomationExternEvent) => {
  emit('external-event', event);
};

const handleScrollBottom = () => {
  emit('scroll-bottom');
};
</script>

<template>
  <ChatMessageWrapper :previous="previous" :next="next" :message="message">
    <!-- Without some content template does not gets rendered -->
    <div>
      <!-- empty div -->
    </div>

    <template #actions>
      <!-- Bet transactions -->
      <BetTransactionMessage
        v-if="isBetTransaction"
        :transaction-subtypes="transactionSubtypes"
        :connect-config="connectConfig"
        :can-submit="canSubmit"
        :show-event-selection="showEventSelection"
        :pre-selected-transactions="preSelectedBets"
        :cancelable="cancelable"
        :single-select-only="props.message.singleSelectOnly ?? false"
        @submit="handleSubmit"
        @scroll-bottom="handleScrollBottom"
      />

      <!-- Payment transactions (deposits/withdrawals) -->
      <PaymentTransactionMessage
        v-else
        :transaction-subtypes="transactionSubtypes"
        :connect-config="connectConfig"
        :can-submit="canSubmit"
        :pre-selected-transactions="preSelectedTransactions"
        :cancelable="cancelable"
        :single-select-only="props.message.singleSelectOnly ?? false"
        @submit="handleSubmit"
        @metadata-automation="handleMetadataAutomation"
        @scroll-bottom="handleScrollBottom"
      />
    </template>
  </ChatMessageWrapper>
</template>
